import {
  Building2Icon,
  SproutIcon,
  HardHatIcon,
  WrenchIcon,
  HomeIcon,
  TrophyIcon,
  CameraIcon,
  MapIcon,
  SmartphoneIcon,
  BoxesIcon,
  ScanLineIcon,
  type LucideIcon,
} from 'lucide-react';

export interface PurposeFeature {
  icon: LucideIcon;
  title: string;
  description: string;
  to: string;
  cta: string;
}

export interface PurposeScenarioStep {
  title: string;
  description: string;
}

export interface Purpose {
  slug: string;
  name: string;
  shortName: string;
  icon: LucideIcon;
  tagline: string;
  /** Participant-facing hero (top of page). */
  heroBadge: string;
  heroHeadline: string;
  heroAccent: string;
  heroSubhead: string;
  scenarioTitle: string;
  scenarioIntro: string;
  scenarioSteps: PurposeScenarioStep[];
  features: PurposeFeature[];
  primaryCtaLabel: string;
  primaryCtaTo: string;
  /** Organiser-facing context (rendered further down the page). */
  organiserBadge: string;
  organiserHeading: string;
  organiserSubhead: string;
}

export const PURPOSES: Purpose[] = [
  {
    slug: 'councils',
    name: 'Local Government & Councils',
    shortName: 'Councils',
    icon: Building2Icon,
    tagline: 'Report and track infrastructure issues across your municipality.',
    heroBadge: 'For residents',
    heroHeadline: 'Spotted a problem?',
    heroAccent: 'Report it.',
    heroSubhead:
      'See a broken footpath, faulty streetlight, or damaged playground equipment? Snap a photo, pin the spot, and send it straight to the council — no account needed.',
    scenarioTitle: 'A broken footpath near a school crossing',
    scenarioIntro:
      "You're walking past a primary school and notice a cracked slab on the footpath that could trip someone.",
    scenarioSteps: [
      { title: 'Snap a photo', description: 'Capture the damaged slab with your phone camera in seconds.' },
      { title: 'Pin the location', description: 'GPS tags the exact spot on the map for the maintenance crew.' },
      { title: 'Submit instantly', description: 'No account needed — the report routes to the right team automatically.' },
      { title: 'Review to resolution', description: 'Follow the status from acknowledged to repaired and closed.' },
    ],
    features: [
      {
        icon: CameraIcon,
        title: 'Report Issues',
        description:
          'Photograph broken signage, footpaths, playground equipment, or streetlights and submit in under a minute — no account required.',
        to: '/report?purpose=councils',
        cta: 'Start a Report',
      },
      {
        icon: SmartphoneIcon,
        title: 'AR Asset View',
        description:
          'Point your phone at a streetlight, bench, or sign to identify the asset, view its product details and maintenance history, and report a fault directly.',
        to: '/ar',
        cta: 'Launch AR',
      },
      {
        icon: MapIcon,
        title: 'Map',
        description:
          'See all reported issues, council boundaries, and infrastructure assets on an interactive map of your municipality.',
        to: '/map?lat=-37.8136&lng=144.9631&zoom=12&purpose=councils',
        cta: 'Open Map',
      },
    ],
    primaryCtaLabel: 'Report an Issue',
    primaryCtaTo: '/report?purpose=councils',
    organiserBadge: 'For councils & local government',
    organiserHeading: 'Keep your community moving.',
    organiserSubhead:
      'From broken footpaths to faulty streetlights — give residents a frictionless way to report infrastructure issues the moment they spot them, and route every report to the right maintenance crew automatically.',
  },
  {
    slug: 'farms',
    name: 'Agriculture & Farming',
    shortName: 'Farms',
    icon: SproutIcon,
    tagline: 'Track damage to fencing, irrigation, and equipment before it costs you.',
    heroBadge: 'For farm workers & contractors',
    heroHeadline: 'Found damage?',
    heroAccent: 'Report it fast.',
    heroSubhead:
      'Storm-damaged fence, broken irrigation line, faulty machinery — photograph it, pin the GPS location, and get it to the right person in seconds. No account needed.',
    scenarioTitle: 'Storm-damaged boundary fence',
    scenarioIntro:
      "After a storm, you're checking the boundary and discover a section of fence has blown down where livestock could get through.",
    scenarioSteps: [
      { title: 'Snap a photo', description: 'Photograph the broken posts and wire where the fence has failed.' },
      { title: 'Pin the location', description: 'Drop a pin at the exact GPS coordinate along the boundary line.' },
      { title: 'Assign the fix', description: 'Route the report to your farmhand or contractor with full context and photos.' },
      { title: 'Review to completion', description: 'Monitor progress until the fence is secure and livestock-safe.' },
    ],
    features: [
      {
        icon: CameraIcon,
        title: 'Report Issues',
        description:
          'Photograph damaged fencing, irrigation pipes, gates, or machinery and submit with GPS coordinates — no account required.',
        to: '/report?purpose=farms',
        cta: 'Start a Report',
      },
      {
        icon: SmartphoneIcon,
        title: 'AR Asset View',
        description:
          'Point your phone at irrigation infrastructure, machinery, or fencing to identify the asset, check its service history, and log a maintenance issue.',
        to: '/ar',
        cta: 'Launch AR',
      },
      {
        icon: BoxesIcon,
        title: 'Circle T Farm Twin',
        description:
          'Explore your property in 3D with mapped boundaries, sheds, internal lanes, and farm assets.',
        to: '/twin/locations/circle-t-farm',
        cta: 'Open Farm Twin',
      },
    ],
    primaryCtaLabel: 'Report an Issue',
    primaryCtaTo: '/report?purpose=farms',
    organiserBadge: 'For farms & agriculture',
    organiserHeading: 'Protect your farm assets.',
    organiserSubhead:
      'Storm-damaged fences, broken irrigation lines, faulty machinery — give your team a fast way to report issues the moment they find them and get them fixed before they escalate.',
  },
  {
    slug: 'workplace-safety',
    name: 'Workplace Health & Safety',
    shortName: 'Workplace Safety',
    icon: HardHatIcon,
    tagline: 'Report OH&S risks before they become incidents.',
    heroBadge: 'For every employee',
    heroHeadline: 'See a hazard?',
    heroAccent: 'Report it now.',
    heroSubhead:
      'Frayed cord, blocked exit, unstable shelving — snap a photo, tag the spot, and alert your safety officer instantly. No account needed.',
    scenarioTitle: 'A frayed power cord in a busy warehouse',
    scenarioIntro:
      "You're walking through the warehouse and spot a frayed extension cord lying across a high-traffic walkway.",
    scenarioSteps: [
      { title: 'Snap a photo', description: 'Capture the hazard before someone trips or gets a shock.' },
      { title: 'Pin the location', description: 'Tag the exact aisle and zone on the site map.' },
      { title: 'Alert the safety officer', description: 'The report reaches the right person for immediate action.' },
      { title: 'Review to resolution', description: 'Follow the fix from hazard logged to cord replaced and signed off.' },
    ],
    features: [
      {
        icon: CameraIcon,
        title: 'Report Hazards',
        description:
          'Photograph frayed cords, blocked exits, spills, or damaged equipment and submit instantly — any employee, no account needed.',
        to: '/report?purpose=workplace-safety',
        cta: 'Report a Hazard',
      },
      {
        icon: SmartphoneIcon,
        title: 'AR Asset View',
        description:
          'Point your phone at equipment, safety fixtures, or structural elements to identify them, verify inspection dates, and flag a hazard on the spot.',
        to: '/ar',
        cta: 'Launch AR',
      },
      {
        icon: MapIcon,
        title: 'Map',
        description:
          'See all reported hazards and safety assets across your site on an interactive map — spot patterns before they become incidents.',
        to: '/map?lat=-37.8136&lng=144.9631&zoom=14&purpose=workplace-safety',
        cta: 'Open Map',
      },
    ],
    primaryCtaLabel: 'Report a Hazard',
    primaryCtaTo: '/report?purpose=workplace-safety',
    organiserBadge: 'For workplace health & safety',
    organiserHeading: 'Spot hazards. Act fast.',
    organiserSubhead:
      'Empower every employee to report safety risks in seconds and route them to the right person for immediate action — before a near-miss becomes an incident.',
  },
  {
    slug: 'facilities',
    name: 'Facilities & Utilities',
    shortName: 'Facilities',
    icon: WrenchIcon,
    tagline: 'Track issues across facilities and utility infrastructure.',
    heroBadge: 'For site teams',
    heroHeadline: 'Spot a maintenance issue?',
    heroAccent: 'Log it instantly.',
    heroSubhead:
      'Leaking valve, faulty panel, broken pump — photograph it, tag the asset, and route it to maintenance with full context in seconds. No account needed.',
    scenarioTitle: 'A leaking valve in a plant room',
    scenarioIntro:
      'During a routine check, you find a valve weeping water onto the floor of a plant room.',
    scenarioSteps: [
      { title: 'Snap a photo', description: 'Photograph the valve and the surrounding water damage.' },
      { title: 'Pin the location', description: 'Tag the plant room and the specific asset on the site map.' },
      { title: 'Route to maintenance', description: 'The report includes asset details so the crew arrives prepared.' },
      { title: 'Review to completion', description: 'Follow the repair from assigned to tested and closed.' },
    ],
    features: [
      {
        icon: CameraIcon,
        title: 'Report Issues',
        description:
          'Photograph leaking valves, faulty panels, broken pumps, or damaged ducting and submit with full location context — no account required.',
        to: '/report?purpose=facilities',
        cta: 'Start a Report',
      },
      {
        icon: SmartphoneIcon,
        title: 'AR Asset View',
        description:
          'Point your phone at valves, panels, pumps, or ducting to identify the asset, pull up its spec sheet and service history, and log a fault with full context.',
        to: '/ar',
        cta: 'Launch AR',
      },
      {
        icon: MapIcon,
        title: 'Map',
        description:
          'Map every asset across your facilities — plant rooms, utility runs, building systems — and track reported issues in real time.',
        to: '/map?lat=-37.8136&lng=144.9631&zoom=14&purpose=facilities',
        cta: 'Open Map',
      },
    ],
    primaryCtaLabel: 'Report an Issue',
    primaryCtaTo: '/report?purpose=facilities',
    organiserBadge: 'For facilities & utilities',
    organiserHeading: 'Manage every asset on site.',
    organiserSubhead:
      'Leaking valves, faulty switchboards, broken HVAC — give your team a way to report maintenance issues across your facilities and utility infrastructure with full asset context, so the crew arrives prepared.',
  },
  {
    slug: 'homes-rentals',
    name: 'Homes & Rental Properties',
    shortName: 'Homes & Rentals',
    icon: HomeIcon,
    tagline: 'Report and track maintenance issues at your own home or a rental you manage.',
    heroBadge: 'For homeowners, tenants & landlords',
    heroHeadline: 'Something needs fixing?',
    heroAccent: 'Report it today.',
    heroSubhead:
      'Leaking tap, cracked tile, broken fence — photograph it, pin the room, and track the repair through to done. No account needed.',
    scenarioTitle: 'A leaking tap reported by a tenant',
    scenarioIntro:
      'Your kitchen tap has been dripping all week. You log it on the spot with a photo and route it to your plumber.',
    scenarioSteps: [
      { title: 'Snap a photo', description: 'Photograph the dripping tap and any water damage underneath the sink.' },
      { title: 'Pin the location', description: 'Tag the property and the specific room so the plumber knows exactly where to look.' },
      { title: 'Assign the fix', description: 'Route the report to your plumber or handyman with full context, photos, and notes.' },
      { title: 'Review to resolution', description: 'Follow the repair from scheduled to fixed and signed off — and share updates with the tenant.' },
    ],
    features: [
      {
        icon: CameraIcon,
        title: 'Report Issues',
        description:
          'Photograph a leaking tap, cracked tile, broken fence, or faulty hot water system and submit in under a minute — no account required.',
        to: '/report?purpose=homes-rentals',
        cta: 'Start a Report',
      },
      {
        icon: SmartphoneIcon,
        title: 'AR Asset View',
        description:
          'Point your phone at a water heater, air conditioner, or appliance to identify the model, check its service history, and log a maintenance issue on the spot.',
        to: '/ar',
        cta: 'Launch AR',
      },
      {
        icon: MapIcon,
        title: 'Map',
        description:
          'Map every asset across your home or rental portfolio — appliances, fixtures, fences — and see reported issues at a glance, property by property.',
        to: '/map?lat=-36.8636969&lng=147.2845645&zoom=17&purpose=homes-rentals',
        cta: 'Open Map',
      },
    ],
    primaryCtaLabel: 'Report an Issue',
    primaryCtaTo: '/report?purpose=homes-rentals',
    organiserBadge: 'For homeowners & landlords',
    organiserHeading: 'Keep your home in good repair.',
    organiserSubhead:
      'Whether you own your home or manage a rental property, give tenants and household members a simple way to report maintenance issues the moment they arise and track them through to resolution.',
  },
  {
    slug: 'treasure-hunt',
    name: 'Events & Treasure Hunts',
    shortName: 'Treasure Hunt',
    icon: TrophyIcon,
    tagline: 'Live hunts for conferences, community events and team days',
    heroBadge: 'Join the hunt',
    heroHeadline: 'Scan. Compete.',
    heroAccent: 'Win.',
    heroSubhead:
      'Grab your phone, scan a QR poster at the ALGIM Learning Exchange 2026, and race to capture 10 exhibitor booths and session rooms, complete CV scan challenges, and climb the live leaderboard — no app download, no sign-up required.',
    scenarioTitle: 'A conference treasure hunt across the venue',
    scenarioIntro:
      'QR posters are placed at stations around the venue. You discover them throughout the day and race to complete the hunt first.',
    scenarioSteps: [
      { title: 'Scan a station QR poster', description: 'Point a phone camera at any QR poster placed around the venue to open the hunt directly — no app install needed.' },
      { title: 'Join with a nickname', description: 'Pick a name and you are in. No account, no sign-up form — the hunt starts the moment you scan.' },
      { title: 'Capture stations & challenges', description: 'Check in at QR and GPS stations, then complete challenges: file a demo report, view an asset in AR, find a point on the map, answer a quiz, and scan exhibitor booth signage with the CV scanner.' },
      { title: 'Climb the leaderboard', description: 'Every capture and challenge earns points. Watch the live leaderboard update in real time and earn the completion bonus for finishing the hunt.' },
    ],
    features: [
      {
        icon: BoxesIcon,
        title: '3D Digital Twin',
        description:
          'Explore the ALGIM Learning Exchange 2026 conference floor in 3D — walk through the venue, hunt stations, and interactive image targets.',
        to: '/events/algim-2026-exchange/twin',
        cta: 'Open 3D Twin',
      },
      {
        icon: ScanLineIcon,
        title: 'CV Scan Challenge',
        description:
          'Point your phone camera at trained exhibitor signage and let the computer vision scanner auto-complete booth challenges with real-time bounding boxes and confidence scores.',
        to: '/ar',
        cta: 'Launch Scanner',
      },
      {
        icon: MapIcon,
        title: 'Map Find Challenge',
        description:
          'Hunters use the live map to locate stations, find a hidden point, and navigate the venue — the same map that shows every reported issue and asset.',
        to: '/map?lat=-37.8136&lng=144.9631&zoom=14&purpose=treasure-hunt',
        cta: 'Open Map',
      },
    ],
    primaryCtaLabel: 'Explore the 3D Twin',
    primaryCtaTo: '/events/algim-2026-exchange/twin',
    organiserBadge: 'For event organisers',
    organiserHeading: 'Turn your event into a live hunt.',
    organiserSubhead:
      'Place QR posters at stations around your venue and participants join instantly — scanning, capturing QR and GPS stations, completing challenges, and climbing a real-time leaderboard. No app download, no accounts, no setup friction for attendees.',
  },
];

export function getPurposeBySlug(slug: string): Purpose | undefined {
  return PURPOSES.find((p) => p.slug === slug);
}
